import { BufferGeometry, Material, Mesh, NearestFilter, Object3D, Texture } from 'three'
import {
  estimateGltfTextureBytes,
  estimateThreeTextureBytes,
} from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { enumerateMaterialTextures, MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type { TileContentPackage, TileResourceCosts } from '@/plugins/tiles/contracts'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('tiles:mesh:resources')

export interface MeshTileContentPackage extends TileContentPackage {
  readonly disposed: boolean
  /** 桥接代理借用包内资源，先退出代理再最终释放源资源。 */
  onDispose(listener: () => void): () => void
}

/** 单一包所有者；解析 Promise 失败后仍能接收并释放迟到的 Three 资源。 */
export class MeshContentResources {
  readonly tracker = new MeshResourceTracker()
  private readonly _images = new Set<TexImageSource>()
  private readonly _released = new WeakSet<object>()
  private readonly _listeners = new Set<() => void>()
  private _disposed = false
  private _retirement?: Iterator<() => void>

  get disposed(): boolean {
    return this._disposed
  }

  onDispose(listener: () => void): () => void {
    if (this._disposed) listener()
    else this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  track<T>(resource: T): T {
    if (resource instanceof Object3D) {
      resource.traverse((object) => {
        if (object instanceof Mesh) {
          this.track(object.geometry)
          this.track(object.material)
        }
      })
    } else if (Array.isArray(resource)) {
      for (const item of resource) this.track(item)
    } else if (resource instanceof Material) {
      for (const texture of enumerateMaterialTextures(resource)) this.track(texture)
      if (this._disposed) this.releaseLate(resource)
      else this.tracker.materials.add(resource)
    } else if (resource instanceof Texture) {
      if (resource.image) this.trackImage(resource.image as TexImageSource)
      if (this._disposed) this.releaseLate(resource)
      else this.tracker.textures.add(resource)
    } else if (resource instanceof BufferGeometry) {
      if (this._disposed) this.releaseLate(resource)
      else this.tracker.geometries.add(resource)
    }
    return resource
  }

  trackImage<T extends TexImageSource>(image: T): T {
    if (this._disposed) this.closeImage(image)
    else this._images.add(image)
    return image
  }

  costs(inputBytes = 0, transientBytes = 0): TileResourceCosts {
    const arrays = new Set<ArrayBufferView>()
    for (const geometry of this.tracker.geometries) {
      for (const attribute of [...Object.values(geometry.attributes), geometry.index]) {
        if (!attribute) continue
        arrays.add('data' in attribute ? attribute.data.array : attribute.array)
      }
    }
    let geometryBytes = 0
    for (const array of arrays) geometryBytes += array.byteLength
    const backingBuffers = new Set([...arrays].map((array) => array.buffer))
    let retainedInputBytes = 0
    for (const buffer of backingBuffers) retainedInputBytes += buffer.byteLength
    let gpuTextureBytes = 0
    for (const texture of this.tracker.textures)
      gpuTextureBytes += estimateThreeTextureBytes(texture)
    // 位图保留至包释放，CPU RGBA 与 GPU 纹理分别计入稳态纹理成本，不保留解码峰值。
    let cpuTextureBytes = 0
    for (const image of this._images)
      cpuTextureBytes += estimateGltfTextureBytes(image, NearestFilter, false)
    return {
      memory: {
        cpuBytes: Math.max(inputBytes, retainedInputBytes) + cpuTextureBytes + transientBytes,
        gpuBytes: geometryBytes + gpuTextureBytes,
      },
      inputBytes: Math.max(inputBytes, retainedInputBytes),
      geometryBytes,
      textureBytes: cpuTextureBytes + gpuTextureBytes,
      transientBytes,
      cpuTextureBytes,
      gpuTextureBytes,
    }
  }

  /** 同步交换外观所有权，几何与来源身份保持不变。 */
  swapAppearance(next: MeshContentResources): MeshContentResources {
    if (this._disposed || next.disposed) throw new Error('不能交换已释放的图片资源')
    const previous = new MeshContentResources()
    for (const key of ['materials', 'textures'] as const) {
      for (const item of this.tracker[key]) previous.tracker[key].add(item as never)
      this.tracker[key].clear()
      for (const item of next.tracker[key]) this.tracker[key].add(item as never)
      next.tracker[key].clear()
    }
    for (const image of this._images) previous._images.add(image)
    this._images.clear()
    for (const image of next._images) this._images.add(image)
    next._images.clear()
    return previous
  }

  dispose(): void {
    const steps = this.createRetirement()
    const errors: unknown[] = []
    for (let next = steps.next(); !next.done; next = steps.next()) {
      try {
        next.value()
      } catch (error) {
        errors.push(error)
      }
    }
    if (errors.length) throw new AggregateError(errors, `Tiles Mesh 资源清理失败: ${errors.length}`)
  }

  createRetirement(): Iterator<() => void> {
    if (this._retirement) return this._retirement
    this._disposed = true
    this._retirement = this.releaseSteps()
    return this._retirement
  }

  private *releaseSteps(): Generator<() => void> {
    for (const listener of this._listeners) {
      this._listeners.delete(listener)
      yield listener
    }
    for (const resources of [
      this.tracker.geometries,
      this.tracker.materials,
      this.tracker.textures,
      this.tracker.others,
    ]) {
      for (const resource of resources) {
        resources.delete(resource as never)
        if (this._released.has(resource)) continue
        this._released.add(resource)
        yield () => resource.dispose()
      }
    }
    for (const image of this._images) {
      this._images.delete(image)
      yield () => this.closeImage(image)
    }
    yield () => this.tracker.dispose()
  }

  /** 取消回调和迟到结果不能用清理错误中断其余异步回收。 */
  disposeSafely(): void {
    try {
      this.dispose()
    } catch (error) {
      log.warn(`内容清理失败: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  private releaseLate(resource: { dispose(): void }): void {
    if (this._released.has(resource)) return
    this._released.add(resource)
    try {
      resource.dispose()
    } catch (error) {
      log.warn(`迟到资源清理失败: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  private closeImage(image: TexImageSource): void {
    if (this._released.has(image)) return
    this._released.add(image)
    if ('close' in image && typeof image.close === 'function') image.close()
    else if ('getContext' in image) image.width = image.height = 0
  }
}
